import { useState } from "react";
import {
  Archive,
  ArchiveRestore,
  Loader2,
  MoreHorizontal,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import type { PartnerGoal } from "@colab/shared";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useArchiveGoal } from "./use-archive-goal";
import { useDeleteGoal } from "./use-delete-goal";
import { useUpdateGoal } from "./use-update-goal";

export function GoalRowActions({ goal }: { goal: PartnerGoal }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const archiveMutation = useArchiveGoal();
  const updateMutation = useUpdateGoal();
  const deleteMutation = useDeleteGoal();

  const isArchived = goal.is_archived;
  const pending =
    archiveMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending;

  function handleArchiveToggle() {
    if (isArchived) {
      updateMutation.mutate(
        {
          id: goal.id,
          partnerId: goal.partner_id,
          patch: { is_archived: false },
        },
        { onSuccess: () => toast.success("Goal restored") },
      );
    } else {
      archiveMutation.mutate(
        { id: goal.id, partnerId: goal.partner_id },
        { onSuccess: () => toast.success("Goal archived") },
      );
    }
  }

  function handleDelete() {
    deleteMutation.mutate(
      { id: goal.id, partnerId: goal.partner_id },
      {
        onSuccess: () => {
          toast.success("Goal deleted");
          setConfirmDelete(false);
        },
      },
    );
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Goal actions"
            className="rounded-md p-1 text-muted-foreground transition hover:bg-muted hover:text-foreground"
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-44">
          {isArchived ? (
            <DropdownMenuItem onClick={handleArchiveToggle} disabled={pending}>
              <ArchiveRestore className="mr-2 h-3.5 w-3.5" />
              Restore
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem onClick={handleArchiveToggle} disabled={pending}>
              <Archive className="mr-2 h-3.5 w-3.5" />
              Archive
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => setConfirmDelete(true)}
            className="text-destructive focus:text-destructive"
            disabled={pending}
          >
            <Trash2 className="mr-2 h-3.5 w-3.5" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this goal?</AlertDialogTitle>
            <AlertDialogDescription>
              The goal and all of its targets are removed. This can&apos;t be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteMutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                handleDelete();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending && (
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              )}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
